import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import DefaultCard from '../../../components/default/DefaultCard';
import ButtonCreate from '../../../components/buttons/ButtonCreate';
import DefaultModal from '../../../components/default/DefaultModal';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { AppContext } from '../../../contexts/AppContext';
import useSalesOrderDetailService from '../../../app/services/hooks/useSalesOrderDetailService';
import { SalesOrderDetail, SalesOrderDetailFilters } from '../../../app/models/SalesOrderDetail';
import SalesOrderDetailCreate from './SalesOrderDetailCreate';
import SalesOrderDetailDataTable from './tables/SalesOrderDetailDataTable';
import SalesOrderDetailEdit from './SalesOrderDetailEdit';

interface Props {
    salesOrderId: number;
    orderCurrencyId?: number;
    isShow?: boolean;
    onUpdate?: () => void;
}

const SalesOrderDetailContainer = ({ 
    salesOrderId, 
    orderCurrencyId,
    isShow = false,
    onUpdate 
}: Props) => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const {
        getSalesOrderDetails,
        fetchingGetSalesOrderDetails,
        deleteSalesOrderDetail,
        fetchingDeleteSalesOrderDetail,
    } = useSalesOrderDetailService();

    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [salesOrderDetailIdEdit, setSalesOrderDetailIdEdit] = useState<number>(-1);
    const [salesOrderDetails, setSalesOrderDetails] = useState<SalesOrderDetail[]>([]);
    const [totalRows, setTotalRows] = useState<number>(0);

    const [filter, setFilter] = useState<SalesOrderDetailFilters>({
        page: 1,
        per_page: 1000,
        sort: 'position',
        order: 'asc',
        sales_order_id: salesOrderId
    });

    useEffect(() => {
        reloadTable();
    }, [filter, salesOrderId]);

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const showEdit = (detailId: number) => {
        setShowingEdit(true);
        setSalesOrderDetailIdEdit(detailId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setSalesOrderDetailIdEdit(-1);
    };

    const reloadTable = () => {
        getSalesOrderDetails(filter, {
            onSuccess: (response: ServiceResponse) => {
                setSalesOrderDetails(response.data.sales_order_details || []);
                setTotalRows(response.data.total_rows || 0);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
            }
        });
    };

    const destroy = (detailId: number) => {
        const _text = 'Está a punto de eliminar la posición #' + detailId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Sí, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando posición...');
                        
                        deleteSalesOrderDetail(detailId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                
                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });
                                
                                reloadTable();
                                if (onUpdate) onUpdate();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                
                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        // Usuario canceló la acción
                    }
                }
            }
        });
    };

    return (
        <>
            <DefaultCard>
                <div className="h3 mt-0 mb-2 card-title">Posiciones</div>
                
                {!isShow && (
                    <div className="mb-3">
                        <ButtonCreate 
                            callbackCreate={showCreate} 
                            title="Agregar Posición" 
                        />
                    </div>
                )}

                <SalesOrderDetailDataTable
                    salesOrderDetails={salesOrderDetails}
                    totalRows={totalRows}
                    loading={fetchingGetSalesOrderDetails}
                    edit={isShow ? undefined : showEdit}
                    destroy={isShow ? undefined : destroy}
                    filter={filter}
                    setFilter={setFilter}
                    callbackSuccess={reloadTable}
                    onSearch={reloadTable}
                />
            </DefaultCard>

            {showingCreate && (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Agregar Posición"
                    backdrop={true}
                    showFooter={false}
                >
                    <SalesOrderDetailCreate
                        salesOrderId={salesOrderId}
                        orderCurrencyId={orderCurrencyId}
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                            if (onUpdate) onUpdate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            )}

            {showingEdit && (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Posición"
                    backdrop={true}
                    showFooter={false}
                >
                    <SalesOrderDetailEdit
                        salesOrderId={salesOrderId}
                        salesOrderDetailId={salesOrderDetailIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                            if (onUpdate) onUpdate();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            )}
        </>
    );
};

export default SalesOrderDetailContainer;

